import { z } from "zod";
import type { CommercialOrganization } from "../../../types/commercialOrganizations";

export const createVehicleRentSchema = (
  commercialOrganizations: CommercialOrganization[] = [],
  existingOrgId?: number,
) =>
  z
  .object({
    contract_city_id: z
      .number()
      .int()
      .positive({ message: "validation.legal.city_required" })
      .optional(),
    contract_date: z.date().nullable().optional(),
    commercial_org_id: z.number().optional(),
    commercial_org_name: z.string().optional(),
    commercial_org_bin: z.string().optional(),
    commercial_org_representative: z.string().optional(),
    commercial_org_basis: z.string().optional(),
    commercial_org_name_kk: z.string().optional(),
    commercial_org_phone: z.string().optional(),
    commercial_org_email: z.string().optional(),
    commercial_org_address: z.string().optional(),
    commercial_org_region: z.string().optional(),
    counterparty_bank_name: z.string().min(1, { message: "validation.legal.counterparty_bank_name_required" }),
    counterparty_iban: z.string().min(1, { message: "validation.legal.counterparty_iban_required" }),
    counterparty_bik: z.string().min(1, { message: "validation.legal.counterparty_bik_required" }),
    car_brand: z.string().min(1, { message: "validation.legal.car_brand_required" }),
    car_year: z
      .number()
      .int()
      .positive({ message: "validation.legal.car_year_required" })
      .optional(),
    car_vin: z.string().min(1, { message: "validation.legal.car_vin_required" }),
    car_plate: z.string().min(1, { message: "validation.legal.car_plate_required" }),
    car_color: z.string().min(1, { message: "validation.legal.car_color_required" }),
    rental_term_months: z
      .number()
      .int()
      .positive({ message: "validation.legal.rental_term_text_required" })
      .optional(),
    rental_amount: z
      .string()
      .min(1, { message: "validation.legal.rental_amount_required" })
      .regex(/^\d+(\.\d+)?$/, { message: "validation.legal.rental_amount_invalid" })
      .optional(),
  })
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_name && data.commercial_org_name.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_name_required",
      path: ["commercial_org_name"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_bin && /^\d{12}$/.test(data.commercial_org_bin);
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_bin_invalid",
      path: ["commercial_org_bin"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_representative && data.commercial_org_representative.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_representative_required",
      path: ["commercial_org_representative"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_name_kk && data.commercial_org_name_kk.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_name_kk_required",
      path: ["commercial_org_name_kk"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_basis && data.commercial_org_basis.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_basis_required",
      path: ["commercial_org_basis"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_phone && data.commercial_org_phone.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_phone_required",
      path: ["commercial_org_phone"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_email && data.commercial_org_email.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_email_required",
      path: ["commercial_org_email"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_address && data.commercial_org_address.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_address_required",
      path: ["commercial_org_address"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_region && data.commercial_org_region.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_region_required",
      path: ["commercial_org_region"],
    },
  )
  .refine(
    (data) => {
      if ((!data.commercial_org_id || data.commercial_org_id === 0) && data.commercial_org_bin) {
        const existingOrg = commercialOrganizations.find(
          (org) => org.bin === data.commercial_org_bin && (!existingOrgId || org.id !== existingOrgId),
        );
        return !existingOrg;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_bin_duplicate",
      path: ["commercial_org_bin"],
    },
  )
  .refine(
    (data) => {
      if ((!data.commercial_org_id || data.commercial_org_id === 0) && data.commercial_org_name) {
        const existingOrg = commercialOrganizations.find(
          (org) =>
            org.name_ru.toLowerCase().trim() === data.commercial_org_name?.toLowerCase().trim() &&
            (!existingOrgId || org.id !== existingOrgId),
        );
        return !existingOrg;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_name_duplicate",
      path: ["commercial_org_name"],
    },
  )
  .refine(
    (data) => {
      if ((!data.commercial_org_id || data.commercial_org_id === 0) && data.commercial_org_name_kk) {
        const existingOrg = commercialOrganizations.find(
          (org) =>
            org.name_kk.toLowerCase().trim() === data.commercial_org_name_kk?.toLowerCase().trim() &&
            (!existingOrgId || org.id !== existingOrgId),
        );
        return !existingOrg;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_name_kk_duplicate",
      path: ["commercial_org_name_kk"],
    },
  );

export const vehicleRentSchema = createVehicleRentSchema();
